/* Agent Config — the guardrail dashboard for the Agent Desk. One shared panel
   body (caps as $ sliders scaled to funded balance, tier presets, kill switch,
   circuit breaker, allow/deny lists) + the two overlay wrappers that mount it:
   AgentConfigSheet (mobile bottom sheet, MoreSheet pattern) and
   AgentConfigDialog (desktop centered overlay).

   Data flows through the useAgentConfig() hook result, passed in as `cfg` by
   the mounting desk page (which also reads cfg.config.kill_switch for its
   halt line). Sliders edit a local DRAFT — nothing autosaves except the kill
   switch, which writes immediately (cfg.killSwitch, optimistic). The server
   only accepts these writes from a session token; the agent's API key can
   read the numbers but never change them.

   Controls adapted from Ben's studio registry (benscomponents.vercel.app —
   slider, hold-to-commit, radio-card-group, tags-input, number-field, meter),
   already written in --studio-* tokens. Amber budget: fills are thin
   lines/marks (slider tracks, ring stroke, pills) — never surfaces. Danger
   red is semantic only: the halted state and the deny list. */
import { useEffect, useRef, useState } from 'react'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const NUM = { ...Mono, fontVariantNumeric: 'tabular-nums' }
const LBL = { ...Mono, fontSize: 9.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', textTransform: 'uppercase' }
const SEC = { ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', textTransform: 'uppercase' }

function fmtMoney(v, digits = 0) {
  if (v == null || !Number.isFinite(v)) return '—'
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
}

/* Tier presets — fractions of the funded balance, straight from
   docs/agent-safety/GUARDRAILS.md (per-trade / daily / concentration /
   approval-above). Selecting a card writes these into the four caps. */
const TIER_PRESETS = {
  conservative: { label: 'Conservative', f: [0.05, 0.15, 0.20, 0.05] },
  balanced: { label: 'Balanced', f: [0.10, 0.40, 0.35, 0.15] },
  aggressive: { label: 'Aggressive', f: [0.25, 1.00, 0.60, 0.33] },
}
const CAP_FIELDS = ['per_trade_cap', 'daily_cap', 'concentration_cap', 'approval_threshold']
const CAP_LABELS = {
  per_trade_cap: { label: 'Per-trade cap', sub: 'Max $ per single order' },
  daily_cap: { label: 'Daily volume cap', sub: 'Max total $ traded per day' },
  concentration_cap: { label: 'Concentration cap', sub: 'Max $ in one ticker' },
  approval_threshold: { label: 'Approval threshold', sub: '$0 = every order needs your OK first' },
}

const presetCaps = (tier, funded) =>
  Object.fromEntries(CAP_FIELDS.map((f, i) => [f, Math.round(funded * TIER_PRESETS[tier].f[i])]))

// The editable slice of a config row (kill_switch is the immediate path, not draft).
const pickEditable = (c) => ({
  tier: c.tier ?? 'conservative',
  funded: c.funded,
  per_trade_cap: c.per_trade_cap,
  daily_cap: c.daily_cap,
  concentration_cap: c.concentration_cap,
  approval_threshold: c.approval_threshold,
  circuit_max_trades: c.circuit_max_trades,
  circuit_window_min: c.circuit_window_min,
  allowlist: [...(c.allowlist ?? [])],
  denylist: [...(c.denylist ?? [])],
  whole_share_exception: !!c.whole_share_exception,
})

/* ── slider row (registry `slider` — pointer capture, amber fill + thumb) ── */
function SliderRow({ label, sub, value, max, step, onChange, disabled, pctOf }) {
  const ref = useRef(null)
  const pct = max > 0 && value != null ? Math.max(0, Math.min(100, (value / max) * 100)) : 0
  const pick = (e) => {
    if (disabled || !ref.current || max <= 0) return
    const r = ref.current.getBoundingClientRect()
    const raw = Math.max(0, Math.min(max, ((e.clientX - r.left) / r.width) * max))
    onChange(Math.round(raw / step) * step)
  }
  return (
    <div style={{ opacity: disabled ? 0.4 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={LBL}>{label}</span>
        <span style={{ ...NUM, fontSize: 12.5, color: 'hsl(var(--studio-accent))' }}>
          {fmtMoney(value)}
          {pctOf > 0 && value != null && (
            <span style={{ color: 'hsl(var(--studio-text-subtle))', marginLeft: 6 }}>{Math.round((value / pctOf) * 100)}%</span>
          )}
        </span>
      </div>
      {sub && <div style={{ ...Sans, fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>{sub}</div>}
      <div
        ref={ref}
        onPointerDown={(e) => { if (disabled) return; e.currentTarget.setPointerCapture(e.pointerId); pick(e) }}
        onPointerMove={(e) => { if (e.buttons !== 0) pick(e) }}
        style={{
          position: 'relative', height: 6, borderRadius: 3, marginTop: 10,
          background: 'hsl(var(--studio-surface-2))', cursor: disabled ? 'default' : 'pointer',
          userSelect: 'none', touchAction: 'none',
        }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, borderRadius: 3, background: 'hsl(var(--studio-accent))', pointerEvents: 'none', transition: 'width 30ms linear' }} />
        <div style={{
          position: 'absolute', top: '50%', left: `${pct}%`, transform: 'translate(-50%, -50%)',
          width: 16, height: 16, borderRadius: '50%', background: 'hsl(var(--studio-accent))',
          border: '2px solid hsl(var(--studio-background))', boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
          pointerEvents: 'none', transition: 'left 30ms linear',
        }} />
      </div>
    </div>
  )
}

/* ── number field with ∓/± steppers (registry `number-field`) ── */
function MoneyField({ label, value, onChange, step = 10, prefix = '$' }) {
  const clamp = (v) => Math.max(0, v)
  const btn = (txt, fn) => (
    <button onClick={fn} style={{
      width: 34, display: 'grid', placeItems: 'center', background: 'transparent', border: 'none',
      color: 'hsl(var(--studio-text-muted))', cursor: 'pointer', fontSize: 16, lineHeight: 1, userSelect: 'none', flexShrink: 0,
    }}>{txt}</button>
  )
  return (
    <div>
      <div style={LBL}>{label}</div>
      <div style={{
        display: 'flex', alignItems: 'stretch', height: 38, marginTop: 8, maxWidth: 220,
        border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-sm)',
        background: 'hsl(var(--studio-surface))', overflow: 'hidden',
      }}>
        {btn('−', () => onChange(clamp((value ?? 0) - step)))}
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center', borderLeft: '1px solid hsl(var(--studio-border))', borderRight: '1px solid hsl(var(--studio-border))' }}>
          <span style={{ ...Mono, fontSize: 12, color: 'hsl(var(--studio-text-muted))', paddingLeft: 8 }}>{prefix}</span>
          <input type="number" inputMode="decimal" value={value ?? ''} placeholder="0"
            onChange={(e) => { const n = parseFloat(e.target.value); onChange(Number.isFinite(n) ? clamp(n) : null) }}
            style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', ...NUM, fontSize: 13.5, color: 'hsl(var(--studio-text))', textAlign: 'center', padding: '0 4px', MozAppearance: 'textfield' }} />
        </div>
        {btn('+', () => onChange(clamp((value ?? 0) + step)))}
      </div>
    </div>
  )
}

function StepField({ label, value, onChange, min = 0, max = 999, suffix }) {
  const clamp = (v) => Math.max(min, Math.min(max, v))
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <span style={LBL}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-sm)', background: 'hsl(var(--studio-surface))', height: 32 }}>
        <button onClick={() => onChange(clamp((value ?? min) - 1))} style={{ width: 32, height: '100%', background: 'transparent', border: 'none', color: 'hsl(var(--studio-text-muted))', cursor: 'pointer', fontSize: 15 }}>−</button>
        <span style={{ ...NUM, fontSize: 13, color: 'hsl(var(--studio-text))', minWidth: 52, textAlign: 'center', borderLeft: '1px solid hsl(var(--studio-border))', borderRight: '1px solid hsl(var(--studio-border))', alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value ?? '—'}{suffix && <span style={{ fontSize: 9.5, color: 'hsl(var(--studio-text-subtle))', marginLeft: 3 }}>{suffix}</span>}
        </span>
        <button onClick={() => onChange(clamp((value ?? min) + 1))} style={{ width: 32, height: '100%', background: 'transparent', border: 'none', color: 'hsl(var(--studio-text-muted))', cursor: 'pointer', fontSize: 15 }}>+</button>
      </div>
    </div>
  )
}

/* ── tier cards (registry `radio-card-group` — amber border + dot) ── */
function TierCards({ tier, isCustom, funded, onSelect }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {Object.entries(TIER_PRESETS).map(([id, t]) => {
        const on = !isCustom && tier === id
        return (
          <button key={id} onClick={() => onSelect(id)} style={{
            display: 'flex', alignItems: 'center', gap: 11, padding: '10px 13px', width: '100%', textAlign: 'left',
            background: on ? 'hsl(var(--studio-accent) / 0.06)' : 'hsl(var(--studio-surface) / 0.5)',
            border: `1px solid ${on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-border))'}`,
            borderRadius: 'var(--studio-radius)', cursor: 'pointer',
            transition: 'border-color 150ms var(--studio-ease), background 150ms var(--studio-ease)',
          }}>
            <span style={{
              width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-border))'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {on && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'hsl(var(--studio-accent))' }} />}
            </span>
            <span style={{ flex: 1 }}>
              <span style={{ ...Mono, fontSize: 12, color: 'hsl(var(--studio-text))', letterSpacing: '0.03em' }}>{t.label}</span>
              <span style={{ ...Mono, display: 'block', fontSize: 9.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 3, letterSpacing: '0.04em' }}>
                {t.f.map((f) => `${Math.round(f * 100)}%`).join(' · ')}
                {funded > 0 && <span style={{ marginLeft: 8, color: 'hsl(var(--studio-text) / 0.45)' }}>{t.f.map((f) => fmtMoney(Math.round(funded * f))).join(' · ')}</span>}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

/* ── ticker tags input (registry `tags-input` — pills, Enter/comma adds) ── */
function TickerTags({ label, sub, value, onChange, tone = '--studio-accent' }) {
  const [input, setInput] = useState('')
  const [focused, setFocused] = useState(false)
  const inputRef = useRef(null)
  const add = (raw) => {
    const t = raw.trim().toUpperCase()
    if (!/^[A-Z0-9.-]{1,10}$/.test(t) || value.includes(t)) { setInput(''); return }
    onChange([...value, t])
    setInput('')
  }
  const remove = (t) => onChange(value.filter((x) => x !== t))
  return (
    <div>
      <div style={LBL}>{label}</div>
      {sub && <div style={{ ...Sans, fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>{sub}</div>}
      <div onClick={() => inputRef.current?.focus()} style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '7px 10px', marginTop: 8,
        border: `1px solid ${focused ? `hsl(var(${tone}))` : 'hsl(var(--studio-border))'}`,
        borderRadius: 'var(--studio-radius)', background: 'hsl(var(--studio-surface))', cursor: 'text',
        transition: 'border-color 150ms ease', minHeight: 40,
      }}>
        {value.map((t) => (
          <span key={t} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999,
            color: `hsl(var(${tone}))`, ...Mono, fontSize: 11.5,
            border: `1px solid hsl(var(${tone}) / 0.4)`,
          }}>
            {t}
            <button onClick={(e) => { e.stopPropagation(); remove(t) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: `hsl(var(${tone}))`, padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
          </span>
        ))}
        <input ref={inputRef} value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ',') && input.trim()) { e.preventDefault(); add(input) }
            if (e.key === 'Backspace' && !input && value.length) remove(value[value.length - 1])
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); if (input.trim()) add(input) }}
          placeholder={value.length === 0 ? 'ADD TICKER' : ''}
          style={{ flex: '1 1 70px', minWidth: 60, border: 'none', outline: 'none', background: 'transparent', ...Mono, fontSize: 12, color: 'hsl(var(--studio-text))', textTransform: 'uppercase' }} />
      </div>
    </div>
  )
}

/* ── hold-to-commit ring (registry `hold-to-commit`) — guards ON→OFF, the
   dangerous direction (re-arming the agent). Releasing early cancels. ── */
function HoldToResume({ onCommit, duration = 1400 }) {
  const [progress, setProgress] = useState(0)
  const rafRef = useRef(null)
  const startRef = useRef(0)
  const start = () => {
    startRef.current = performance.now()
    const loop = (ts) => {
      const p = Math.min(1, (ts - startRef.current) / duration)
      setProgress(p)
      if (p < 1) rafRef.current = requestAnimationFrame(loop)
      else { setProgress(0); onCommit() }
    }
    rafRef.current = requestAnimationFrame(loop)
  }
  const cancel = () => { cancelAnimationFrame(rafRef.current); setProgress(0) }
  useEffect(() => () => cancelAnimationFrame(rafRef.current), [])
  const size = 44, stroke = 3
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  return (
    <div onPointerDown={start} onPointerUp={cancel} onPointerLeave={cancel} onPointerCancel={cancel}
      style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', touchAction: 'none' }}>
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke="hsl(var(--studio-border))" strokeWidth={stroke} fill="none" />
          <circle cx={size / 2} cy={size / 2} r={r} stroke="hsl(var(--studio-accent))" strokeWidth={stroke} fill="none"
            strokeDasharray={circ} strokeDashoffset={circ * (1 - progress)} />
        </svg>
        <span style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', ...Mono, fontSize: 8, color: 'hsl(var(--studio-text-muted))' }}>▶</span>
      </div>
      <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-muted))' }}>HOLD TO RESUME</span>
    </div>
  )
}

/* ── kill switch strip — OFF→ON is one tap (emergencies are fast);
   ON→OFF requires the hold ring. ON = the screen's only danger surface. ── */
function KillStrip({ on, busy, onKill, onResume }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      padding: '13px 14px', borderRadius: 'var(--studio-radius)',
      border: `1px solid ${on ? 'hsl(var(--studio-danger) / 0.6)' : 'hsl(var(--studio-border))'}`,
      background: on ? 'hsl(var(--studio-danger) / 0.08)' : 'hsl(var(--studio-surface) / 0.5)',
      transition: 'border-color 200ms, background 200ms',
    }}>
      <div>
        <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.12em', color: on ? 'hsl(var(--studio-danger))' : 'hsl(var(--studio-text))' }}>
          {on ? 'TRADING HALTED' : 'AGENT TRADING ACTIVE'}
        </div>
        <div style={{ ...Sans, fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 3 }}>
          {on ? 'Your agent reads this before every order and stands down.' : 'Kill switch halts all agent orders immediately.'}
        </div>
      </div>
      {on ? (
        <HoldToResume onCommit={onResume} />
      ) : (
        <button onClick={onKill} disabled={busy} style={{
          ...Mono, fontSize: 10, letterSpacing: '0.14em', padding: '9px 16px', cursor: 'pointer',
          background: 'transparent', color: 'hsl(var(--studio-danger))',
          border: '1px solid hsl(var(--studio-danger) / 0.55)', borderRadius: 'var(--studio-radius-sm)',
          opacity: busy ? 0.5 : 1, flexShrink: 0,
        }}>KILL</button>
      )}
    </div>
  )
}

/* ── daily usage meter (registry `meter` — one bar under the daily cap) ── */
function UsageMeter({ usage, cap }) {
  if (usage?.traded_dollars == null) return null
  const spent = usage.traded_dollars
  const pct = cap > 0 ? Math.min(100, (spent / cap) * 100) : 0
  const maxed = cap > 0 && spent >= cap
  return (
    <div style={{ marginTop: 9 }}>
      <div style={{ height: 4, background: 'hsl(var(--studio-border) / 0.6)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `hsl(var(${maxed ? '--studio-danger' : '--studio-accent'}))`, borderRadius: 2, transition: 'width 600ms var(--studio-ease)' }} />
      </div>
      <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.08em', color: `hsl(var(${maxed ? '--studio-danger' : '--studio-text-subtle'}))`, marginTop: 5 }}>
        TODAY {fmtMoney(spent, 2)}{cap > 0 && ` OF ${fmtMoney(cap)}`} · {usage.trade_count ?? 0} TRADE{usage.trade_count === 1 ? '' : 'S'}{maxed && ' · CAP REACHED'}
      </div>
    </div>
  )
}

function SectionHead({ children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderTop: '1px solid hsl(var(--studio-border) / 0.4)', paddingTop: 16, marginTop: 20 }}>
      <span style={SEC}>{children}</span>
      {right}
    </div>
  )
}

/* ── the shared panel body ──
   `syncedEquity` is the desk's real account equity (agent_account_state via
   useAgentPortfolio). It SEEDS the funded balance when the user has never set
   one — no second number to type — and the tier presets fill the caps off it,
   so a fresh user's save is one tap. It is a default, not a binding: caps stay
   human-owned numbers that don't drift with the market, and typing overrides. */
export function AgentConfigPanel({ cfg, syncedEquity }) {
  const { config, usage, loading, saving, save, killSwitch, signedIn } = cfg
  const [draft, setDraft] = useState(null)
  const [status, setStatus] = useState(null)   // { ok, text } transient save feedback
  const statusTimer = useRef(null)
  useEffect(() => () => clearTimeout(statusTimer.current), [])
  useEffect(() => {
    if (!config || draft) return
    const seeded = pickEditable(config)
    if (seeded.funded == null && Number.isFinite(syncedEquity) && syncedEquity > 0) {
      seeded.funded = Math.round(syncedEquity)
      Object.assign(seeded, presetCaps(seeded.tier, seeded.funded))
    }
    setDraft(seeded)
  }, [config, draft, syncedEquity])

  if (!signedIn) return <div style={{ ...Mono, fontSize: 10.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', padding: '36px 0', textAlign: 'center' }}>SIGN IN TO CONFIGURE YOUR AGENT</div>
  if (loading || !config || !draft) return <div style={{ ...Mono, fontSize: 10.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', padding: '36px 0', textAlign: 'center' }}>LOADING CONFIG…</div>

  const funded = draft.funded ?? 0
  const sliderStep = Math.max(1, Math.round(funded / 200))
  // No row saved yet (server returns tier defaults with updated_at null). Until
  // a human saves, every cap the agent reads is null — and an agent following
  // GUARDRAILS.md ("when in doubt, do nothing") refuses to trade. That gate is
  // deliberate; this banner makes it visible instead of a silent no-op.
  const unconfigured = !config.updated_at
  // CUSTOM when any cap deviates from the active tier's preset for this funding.
  const preset = funded > 0 ? presetCaps(draft.tier, funded) : null
  const isCustom = !!preset && CAP_FIELDS.some((f) => draft[f] != null && draft[f] !== preset[f])
  const dirty = JSON.stringify(draft) !== JSON.stringify(pickEditable(config))

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))
  const flash = (ok, text) => {
    setStatus({ ok, text })
    clearTimeout(statusTimer.current)
    statusTimer.current = setTimeout(() => setStatus(null), ok ? 1800 : 4200)
  }
  const doSave = async () => {
    try {
      const saved = await save(draft)
      setDraft(pickEditable(saved))
      flash(true, 'SAVED ✓')
    } catch (e) {
      flash(false, String(e.message || e).toUpperCase())
    }
  }
  const doKill = (on) => killSwitch(on).then(
    () => flash(true, on ? 'HALTED' : 'RESUMED'),
    (e) => flash(false, String(e.message || e).toUpperCase()),
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 16 }}>
        {unconfigured && (
          <div style={{
            padding: '11px 13px', marginBottom: 12, borderRadius: 'var(--studio-radius)',
            border: '1px solid hsl(var(--studio-accent) / 0.5)', background: 'hsl(var(--studio-accent) / 0.06)',
          }}>
            <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.14em', color: 'hsl(var(--studio-accent))' }}>NO LIMITS SET — AGENT WON’T TRADE</div>
            <div style={{ ...Sans, fontSize: 11, lineHeight: 1.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 4 }}>
              Your agent reads its caps here before every order and stands down while they’re unset.
              {funded > 0 ? ' Balance and caps are pre-filled from your synced account — review and hit Save to activate.' : ' Set your funded balance and caps, then hit Save to activate.'}
            </div>
          </div>
        )}
        <KillStrip on={!!config.kill_switch} busy={saving} onKill={() => doKill(true)} onResume={() => doKill(false)} />

        <SectionHead>Funding</SectionHead>
        <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', margin: '4px 0 12px' }}>
          The balance your caps scale against. Pre-filled from your synced account
          {Number.isFinite(syncedEquity) && syncedEquity > 0 ? ` (${fmtMoney(syncedEquity, 2)})` : ''} — override it if you want the agent
          working a smaller slice. It stays fixed until you change it, so caps don't drift with the market.
        </div>
        <MoneyField label="Funded balance" value={draft.funded} onChange={(v) => set({ funded: v })} step={10} />

        <SectionHead>Risk tier</SectionHead>
        <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', margin: '4px 0 12px' }}>
          Per-trade · daily · concentration · approval-above. Picking a tier fills the caps below.
        </div>
        <TierCards tier={draft.tier} isCustom={isCustom} funded={funded}
          onSelect={(id) => set({ tier: id, ...(funded > 0 ? presetCaps(id, funded) : {}) })} />

        <SectionHead right={isCustom
          ? <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.4)', borderRadius: 'var(--studio-radius-sm)', padding: '2px 6px' }}>CUSTOM</span>
          : null}>Caps</SectionHead>
        {funded <= 0 && (
          <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', marginTop: 6 }}>SET FUNDED BALANCE FIRST</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 14 }}>
          {CAP_FIELDS.map((f) => (
            <div key={f}>
              <SliderRow label={CAP_LABELS[f].label} sub={CAP_LABELS[f].sub}
                value={draft[f]} max={funded} step={sliderStep} pctOf={funded}
                disabled={funded <= 0} onChange={(v) => set({ [f]: v })} />
              {f === 'daily_cap' && <UsageMeter usage={usage} cap={draft.daily_cap} />}
            </div>
          ))}
        </div>
        <button onClick={() => set({ whole_share_exception: !draft.whole_share_exception })} style={{
          display: 'flex', alignItems: 'center', gap: 9, marginTop: 18, padding: 0,
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}>
          <span style={{
            width: 14, height: 14, borderRadius: 3, flexShrink: 0,
            border: `1.5px solid ${draft.whole_share_exception ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-border))'}`,
            display: 'grid', placeItems: 'center',
          }}>
            {draft.whole_share_exception && <span style={{ width: 6, height: 6, borderRadius: 1, background: 'hsl(var(--studio-accent))' }} />}
          </span>
          <span>
            <span style={{ ...Mono, fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-text))' }}>WHOLE-SHARE EXCEPTION</span>
            <span style={{ ...Sans, display: 'block', fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>One whole share is allowed even when it exceeds the per-trade cap (small accounts).</span>
          </span>
        </button>

        <SectionHead>Circuit breaker</SectionHead>
        <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', margin: '4px 0 12px' }}>
          Agent stops and asks a human after this many trades inside the window.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <StepField label="Max trades" value={draft.circuit_max_trades} min={1} max={20} onChange={(v) => set({ circuit_max_trades: v })} />
          <StepField label="Window" value={draft.circuit_window_min} min={1} max={240} suffix="MIN" onChange={(v) => set({ circuit_window_min: v })} />
        </div>

        <SectionHead>Lists</SectionHead>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
          <TickerTags label="Allowlist" sub="Empty = any ticker is tradable. Add names to restrict the agent to only these." value={draft.allowlist} onChange={(v) => set({ allowlist: v })} />
          <TickerTags label="Denylist" sub="Never trade these, regardless of anything else." value={draft.denylist} onChange={(v) => set({ denylist: v })} tone="--studio-danger" />
        </div>

        <div style={{ ...Sans, fontSize: 10.5, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', marginTop: 24, borderTop: '1px solid hsl(var(--studio-border) / 0.4)', paddingTop: 12 }}>
          Your agent fetches these numbers at the start of every run and before each order.
          Its API key can read them but can never change them — only you can, here.
          Enforcement is the agent obeying what it reads; the hard stop is disconnecting
          its brokerage access.
        </div>
      </div>

      {/* sticky save bar — explicit save; caps shouldn't flicker mid-drag */}
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, paddingTop: 12,
        borderTop: '1px solid hsl(var(--studio-border) / 0.5)',
      }}>
        <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: `hsl(var(${status ? (status.ok ? '--studio-success' : '--studio-danger') : '--studio-text-subtle'}))` }}>
          {status ? status.text : dirty ? 'UNSAVED CHANGES' : ''}
        </span>
        <button onClick={doSave} disabled={!dirty || saving} style={{
          ...Mono, fontSize: 10.5, letterSpacing: '0.16em', padding: '10px 22px', cursor: dirty && !saving ? 'pointer' : 'default',
          background: 'transparent', color: `hsl(var(${dirty ? '--studio-accent' : '--studio-text-subtle'}))`,
          border: `1px solid ${dirty ? 'hsl(var(--studio-accent) / 0.6)' : 'hsl(var(--studio-border))'}`,
          borderRadius: 'var(--studio-radius-sm)', transition: 'color 150ms, border-color 150ms',
        }}>{saving ? 'SAVING…' : 'SAVE'}</button>
      </div>
    </div>
  )
}

/* ── mobile mount: bottom sheet (MoreSheet pattern from shell.jsx) ── */
export function AgentConfigSheet({ cfg, syncedEquity, onClose }) {
  const [shown, setShown] = useState(false)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 66 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '88%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.5)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden',
      }}>
        <div style={{ padding: '9px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <span onClick={onClose} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 24, color: 'hsl(var(--studio-text))' }}>Agent Config</span>
            <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, padding: '4px 20px calc(14px + env(safe-area-inset-bottom))' }}>
          <AgentConfigPanel cfg={cfg} syncedEquity={syncedEquity} />
        </div>
      </div>
    </div>
  )
}

/* ── desktop mount: centered dialog overlay ── */
export function AgentConfigDialog({ cfg, syncedEquity, onClose }) {
  const [shown, setShown] = useState(false)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 66, display: 'grid', placeItems: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 200ms ease' }} />
      <div style={{
        position: 'relative', width: 'min(560px, 92vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', border: '1px solid hsl(var(--studio-border))',
        borderTop: '2px solid hsl(var(--studio-accent) / 0.5)', borderRadius: 'var(--studio-radius)',
        boxShadow: '0 24px 64px hsl(var(--studio-overlay) / 0.7)',
        opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(10px)', transition: 'opacity 220ms ease, transform 220ms var(--studio-ease)',
        padding: '18px 24px 18px', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 24, color: 'hsl(var(--studio-text))' }}>Agent Config</span>
          <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <AgentConfigPanel cfg={cfg} syncedEquity={syncedEquity} />
        </div>
      </div>
    </div>
  )
}

/* Gear button + halt line, shared by both desk mastheads. */
export function GearButton({ onClick, size = 30 }) {
  return (
    <button onClick={onClick} aria-label="Agent config" style={{
      width: size, height: size, display: 'grid', placeItems: 'center', flexShrink: 0,
      background: 'transparent', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-sm)',
      cursor: 'pointer', color: 'hsl(var(--studio-text-muted))',
    }}>
      <svg width={size - 14} height={size - 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
      </svg>
    </button>
  )
}

export function HaltLine({ style }) {
  return (
    <div style={{
      ...Mono, fontSize: 9.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-danger))',
      padding: '8px 0', borderBottom: '1px solid hsl(var(--studio-danger) / 0.3)', ...style,
    }}>
      ⏻ TRADING HALTED — KILL SWITCH ON
    </div>
  )
}
